import { Document, Font, Page, pdf, StyleSheet, Text, View } from "@react-pdf/renderer";
import { DateTime } from "luxon";
import { Html } from "react-pdf-html";
import { FormRecipientType } from "src/constants";
import type { FormSubmissionDetailDto } from "src/http";
import { getFormSubmissionName } from "./getFormSubmissionName";
import { getFormSubmissionPeriod } from "./getFormSubmissionPeriod";
import { getFormSubmissionViewers } from "./getFormSubmissionViewers";

async function exportFormSubmissionToPdf(formSubmission: FormSubmissionDetailDto): Promise<Blob> {
  Font.register({
    family: "Open Sans",
    src: "/fonts/OpenSans-Regular.ttf",
  });

  Font.register({
    family: "Open Sans",
    src: "/fonts/OpenSans-Bold.ttf",
    fontWeight: "bold",
  });

  const styles = StyleSheet.create({
    document: {
      fontSize: 10,
      fontFamily: "Open Sans",
    },
    page: {
      padding: 20,
    },
    heading1: {
      fontWeight: "bold",
      fontSize: 16,
    },
    heading2: {
      fontWeight: "bold",
      fontSize: 14,
      marginTop: 2,
    },
    heading3: {
      fontWeight: "bold",
      fontSize: 10,
      marginTop: 2,
    },
    inline: {
      flexDirection: "row",
      justifyContent: "flex-start",
      flexWrap: "wrap",
      gap: 5,
    },
    label: {
      fontWeight: "bold",
      textTransform: "uppercase",
    },
    caption: {
      textTransform: "uppercase",
      fontSize: 8,
    },
  });

  const formSubmissionViewers = getFormSubmissionViewers(formSubmission);

  const doc = (
    <Document style={styles.document}>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={{ ...styles.caption, marginBottom: 10 }}>
            Exported {DateTime.utc().toLocal().toLocaleString(DateTime.DATE_MED)}
          </Text>
        </View>
        <View style={{ marginBottom: 10 }}>
          <Text style={styles.heading1}>
            {getFormSubmissionName(
              formSubmission.formName,
              formSubmission.dateRangeStartUtc,
              formSubmission.dateRangeEndUtc,
            )}
          </Text>
        </View>

        {formSubmission.dateRangeStartUtc != null && formSubmission.dateRangeEndUtc != null && (
          <View style={styles.inline}>
            <Text style={styles.label}>Period</Text>
            <Text>{getFormSubmissionPeriod(formSubmission.dateRangeStartUtc, formSubmission.dateRangeEndUtc)}</Text>
          </View>
        )}

        {formSubmission.recipientType !== FormRecipientType.Self && (
          <View style={styles.inline}>
            <Text style={styles.label}>Recipient</Text>
            <Text>{formSubmission.recipientUser.displayName}</Text>
          </View>
        )}

        <View style={styles.inline}>
          <Text style={styles.label}>
            {formSubmission.recipientType === FormRecipientType.Self ? "Creator" : "Sender"}
          </Text>
          <Text>{formSubmission.createdByUser.displayName}</Text>
        </View>

        <View style={formSubmissionViewers.length > 0 ? undefined : styles.inline}>
          <Text style={styles.label}>Visibility</Text>
          {formSubmissionViewers.length > 0 ? (
            formSubmissionViewers
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((x, i) => (
                <Text key={i} style={{ marginLeft: 10 }}>
                  {x.displayName}
                </Text>
              ))
          ) : (
            <Text>Public to Organization</Text>
          )}
        </View>

        <View style={styles.inline}>
          <Text style={styles.label}>Created</Text>
          <Text>
            {DateTime.fromISO(formSubmission.createdDateTimeUtc, { zone: "utc" })
              .toLocal()
              .toLocaleString(DateTime.DATE_MED)}
          </Text>
        </View>

        {formSubmission.submittedDateTimeUtc && (
          <View style={styles.inline}>
            <Text style={styles.label}>Submitted</Text>
            <Text>
              {DateTime.fromISO(formSubmission.submittedDateTimeUtc, { zone: "utc" })
                .toLocal()
                .toLocaleString(DateTime.DATE_MED)}
            </Text>
          </View>
        )}

        {formSubmission.requiresReviewByRecipient && (
          <View style={styles.inline}>
            <Text style={styles.label}>Reviewed</Text>
            <Text>
              {formSubmission.reviewedByRecipientDateTimeUtc == null
                ? "Not reviewed yet."
                : DateTime.fromISO(formSubmission.reviewedByRecipientDateTimeUtc, { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.DATE_MED)}
            </Text>
          </View>
        )}

        {formSubmission.referencedActionItems.length > 0 && (
          <View>
            <Text style={styles.label}>Referenced Strategic Items</Text>
            {formSubmission.referencedActionItems.map((x, i) => (
              <Text key={i} style={{ marginLeft: 10 }}>
                {x.description}
              </Text>
            ))}
          </View>
        )}

        {formSubmission.referencedTaskItems.length > 0 && (
          <View>
            <Text style={styles.label}>Referenced Tasks</Text>
            {formSubmission.referencedTaskItems.map((x, i) => (
              <Text key={i} style={{ marginLeft: 10 }}>
                {x.description}
              </Text>
            ))}
          </View>
        )}

        {formSubmission.questionGroups.map((questionGroup, i) => (
          <View key={i} style={{ marginTop: 15 }} wrap={false}>
            <Text style={styles.heading2}>{questionGroup.name}</Text>
            {questionGroup.questions.map((question, j) => (
              <View key={j} style={{ marginBottom: 5 }}>
                <Text style={styles.heading3}>{question.name}</Text>
                <Html
                  style={{
                    fontSize: 10,
                  }}
                  stylesheet={{
                    p: {
                      margin: 0,
                    },
                    ul: {
                      margin: 0,
                    },
                  }}
                >
                  {question.answer?.value?.toString() ?? "No answer given."}
                </Html>
              </View>
            ))}
          </View>
        ))}
      </Page>
    </Document>
  );

  return pdf(doc).toBlob();
}

export { exportFormSubmissionToPdf };
